import { useCallback, useEffect, useState } from 'react';
import { Intent } from '@blueprintjs/core';
import { useHistory } from 'react-router-dom';
import { useStyles } from './Ledger.styles';
import { useBudgetContext } from '../context';
import {
  BudgetAuthResponse,
  Category,
  LedgerDataItem,
  MessageType,
  PartialLedgerDataItem,
  Subcategory,
} from '../types';
import { LedgerNav } from './LedgerNav';
import {
  addLedgerDataItem,
  deleteLedgerDataItem,
  getMessage,
  sortLedgerData,
  updateItemBalances,
  updateItemCategories,
  updateLedgerDataItem,
} from '../utils/ledger';
import {
  copyYear,
  createCategoryRecord,
  createEntry,
  createSubcategoryRecord,
  deleteYear,
  deleteEntry,
  getBudgetCategories,
  getBudgetGuid,
  getBudgetItems,
  getBudgetSubcategories,
  updateEntry,
  getBudgetStatsForYear,
} from '../utils/api';
import { formatDate, dollarFormat } from "../utils/format";
import { Dialog } from './Dialog';
import { Toaster } from './Toaster';
import { Table } from './Table';
import { APP } from '../constants/app';
import Categories from './Categories';

export const Ledger = () => {
  const classes = useStyles();
  const history = useHistory();
  const {
    budgetGuid,
    setBudgetGuid,
    budgetYear,
    setBudgetYear,
    startingBalance,
    setStartingBalance,
    ledgerData,
    setLedgerData,
    categories,
    setCategories,
    subcategories,
    setSubcategories,
  } = useBudgetContext();
  const defaultDate = new Date();
  defaultDate.setFullYear(budgetYear);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCopying, setIsCopying] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isEmpty, setIsEmpty] = useState<boolean>(false);
  const [nextYearItemCount, setNextYearItemCount] = useState<number>(0);
  const [categoryToCreate, setCategoryToCreate] = useState<Category>();
  const [subcategoryToCreate, setSubcategoryToCreate] = useState<Subcategory>();
  const [itemToCategorize, setItemToCategorize] = useState<LedgerDataItem>();
  const [itemToDelete, setItemToDelete] = useState<LedgerDataItem>();
  const [dialogMessage, setDialogMessage] = useState<string>('');
  const [isCategorizeDialogOpen, setIsCategorizeDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [percentScrolled, setPercentScrolled] = useState<[number, number]>([0, 0]);

  useEffect(() => {
    async function checkBudgetGuid() {
      if (!budgetGuid) {
        const budgetAuthResponse: BudgetAuthResponse = await getBudgetGuid();
        setBudgetGuid(budgetAuthResponse.budgetGuid);
        const categories: Array<Category> = await getBudgetCategories(
          budgetAuthResponse.budgetGuid,
        );

        setCategories(categories);
        const subcategories: Array<Subcategory> = await getBudgetSubcategories(
          budgetAuthResponse.budgetGuid,
        );

        setSubcategories(subcategories);
      }
    }

    checkBudgetGuid();
  }, []);

  const reloadLedgerData = useCallback(async () => {
    setIsLoading(true);
    setLedgerData({ items: [] });
    const stats = await getBudgetStatsForYear(budgetGuid, String(budgetYear));
    const thisYearStartingBalance = stats.startingBalance;
    setStartingBalance(thisYearStartingBalance);
    const newLedgerData = await getBudgetItems(budgetGuid, String(budgetYear));
    if (newLedgerData.items.length) {
      newLedgerData.items = sortLedgerData(newLedgerData);
      newLedgerData.items = updateItemBalances(newLedgerData, thisYearStartingBalance!);
      newLedgerData.items = updateItemCategories(newLedgerData, categories, subcategories);
      setLedgerData(newLedgerData);
      setIsLoading(false);
      setIsEmpty(false);
    } else {
      setIsLoading(false);
      setIsEmpty(true);
    }
    if (new Date().getFullYear() === budgetYear) {
      setNextYearItemCount(stats.nextYearItemCount || 0);
    }
  }, [budgetGuid, budgetYear, setLedgerData]);

  useEffect(() => {
    if (budgetGuid) {
      reloadLedgerData();
      window.document.title = `${budgetYear} Budget`;
    }
  }, [budgetGuid, budgetYear, reloadLedgerData, setLedgerData]);

  const scrollToMonth = (
    target: string,
    event?: React.MouseEvent<HTMLElement, MouseEvent>,
  ): boolean => {
    event?.preventDefault();
    const el = document.getElementById(target);

    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
      return true;
    } else {
      return false;
    }
  };

  const categorize = (event: React.MouseEvent<HTMLElement, MouseEvent>, item: LedgerDataItem) => {
    event.preventDefault();
    setItemToCategorize(item);
    setDialogMessage(getMessage(MessageType.CONFIRM_CATEGORIZATION, item));
    openCategorizeDialog();
  };

  const categorizeItem = async (
    categorizedItem?: PartialLedgerDataItem,
    originalItem?: LedgerDataItem,
  ) => {
    setIsCategorizeDialogOpen(false);
    try {
      if (itemToCategorize) {
        if (categoryToCreate) {
          const newCategory = await createCategoryRecord(budgetGuid, categoryToCreate);
          itemToCategorize.category_guid = newCategory.guid;
          if (subcategoryToCreate) {
            subcategoryToCreate.category_guid = newCategory.guid;
          }
          categories.push(newCategory);
          setCategories(categories);
          setCategoryToCreate(undefined);
        }
        if (subcategoryToCreate) {
          const newSubcategory = await createSubcategoryRecord(
            budgetGuid,
            subcategoryToCreate,
            itemToCategorize.category_guid!,
          );
          itemToCategorize.subcategory_guid = newSubcategory.guid;
          subcategories.push(newSubcategory);
          setSubcategories(subcategories);
          setSubcategoryToCreate(undefined);
        }
        setLedgerData(updateLedgerDataItem(ledgerData, itemToCategorize!, startingBalance!));
        await updateEntry(budgetGuid, itemToCategorize);
        Toaster.show({
          message: getMessage(MessageType.ITEM_CATEGORIZED, itemToCategorize!),
          intent: Intent.SUCCESS,
          icon: 'tick-circle',
        });
      }
    } catch (err) {
      console.error(err);
      // originalItem && updateLedgerDataItem(ledgerData, originalItem);
      Toaster.show({
        message: `An error occurred while trying to categorize the item`,
        intent: Intent.DANGER,
        icon: 'error',
      });
    }
  };

  const openCategorizeDialog = () => setIsCategorizeDialogOpen(true);

  const closeCategorizeDialog = () => {
    setIsCategorizeDialogOpen(false);
    setItemToDelete(undefined);
  };

  const confirmDeletion = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    item: LedgerDataItem,
  ) => {
    event.preventDefault();
    setItemToDelete(item);
    setDialogMessage(getMessage(MessageType.CONFIRM_DELETE, item));
    openDeleteDialog();
  };

  const deleteItem = async () => {
    setIsDeleteDialogOpen(false);
    if (itemToDelete) {
      setLedgerData(deleteLedgerDataItem(ledgerData, itemToDelete, startingBalance!));
      const success = await deleteEntry(budgetGuid, itemToDelete.guid);
      if (success) {
        const deletedItem: LedgerDataItem = itemToDelete;
        const message = getMessage(MessageType.ITEM_DELETED, deletedItem);
        Toaster.show({
          message,
          intent: Intent.SUCCESS,
          icon: 'tick-circle',
        });
        setItemToDelete(undefined);
      } else {
        setLedgerData(ledgerData);
      }
    }
  };

  const openDeleteDialog = () => setIsDeleteDialogOpen(true);

  const closeDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setItemToDelete(undefined);
  };

  const addItem = async (newItem: PartialLedgerDataItem): Promise<boolean> => {
    try {
      const addedItem = await createEntry(budgetGuid, newItem);
      setLedgerData(addLedgerDataItem(ledgerData, addedItem, startingBalance!));
      Toaster.show({
        message: getMessage(MessageType.ITEM_ADDED, addedItem),
        intent: Intent.SUCCESS,
        icon: 'tick-circle',
      });
      return true;
    } catch (err) {
      Toaster.show({
        message: `An error occurred while trying to save the new item`,
        intent: Intent.DANGER,
        icon: 'error',
      });
      return false;
    }
  };

  const editItem = async (editedItem: PartialLedgerDataItem, originalItem?: LedgerDataItem) => {
    try {
      setLedgerData(updateLedgerDataItem(ledgerData, editedItem, startingBalance!));
      const savedItem = await updateEntry(budgetGuid, editedItem);
      Toaster.show({
        message: getMessage(MessageType.ITEM_EDITED, savedItem),
        intent: Intent.SUCCESS,
        icon: 'tick-circle',
      });
    } catch (err) {
      console.error(err);
      originalItem && updateLedgerDataItem(ledgerData, originalItem, startingBalance!);
      Toaster.show({
        message: `An error occurred while trying to save the new item`,
        intent: Intent.DANGER,
        icon: 'error',
      });
    }
  };

  const editMultipleItems = async (editedItem: PartialLedgerDataItem, originalItems: Array<LedgerDataItem>) => {
    try {
      let itemSuccessMessage: string = '';
      const updatedItems = await Promise.all(
        originalItems.filter((originalItem) => !originalItem.paid || originalItem.guid === editedItem.guid)
        .map(async (originalItem) => {
          originalItem.label = editedItem.label || originalItem.label;
          originalItem.amount = editedItem.amount || originalItem.amount;
          if (originalItem.guid === editedItem.guid) {
            originalItem.paid = editedItem.paid;
          }
          setLedgerData(updateLedgerDataItem(ledgerData, originalItem, startingBalance!));
          await updateEntry(budgetGuid, originalItem);
          return originalItem as LedgerDataItem;
        }),
      );
      updatedItems.forEach((item) => {
        itemSuccessMessage += `${formatDate(item.settledDate)}\n`;
      });
      Toaster.show({
        message: `Successfully updated all unpaid, visible transactions to '${editedItem.label}' for ${dollarFormat(editedItem.amount)} on the following dates:\n${itemSuccessMessage}`,
        intent: Intent.SUCCESS,
        icon: 'tick-circle',
      });
    } catch (err) {
      console.error(err);
      Toaster.show({
        message: `An error occurred while trying to save the changes`,
        intent: Intent.DANGER,
        icon: 'error',
      });
    }
  };

  const copyItems = async (fromYear: number, toYear: number) => {
    try {
      setIsLoading(false);
      setIsCopying(true);
      const isCopySuccessful = await copyYear(budgetGuid, fromYear, toYear);
      if (isCopySuccessful) {
        setIsCopying(false);
        Toaster.show({
          message: `Successfully copied items from ${fromYear} to ${toYear}`,
          intent: Intent.SUCCESS,
          icon: 'tick-circle',
        });
        setBudgetYear(toYear);
        history.push(`${APP.ROUTES.LEDGER}/${toYear}`);
      }
    } catch (err) {
      console.error(err);
      setIsCopying(false);
      Toaster.show({
        message: `An error occurred while trying to copy items from ${fromYear} to ${toYear}`,
        intent: Intent.DANGER,
        icon: 'error',
      });
    }
  };

  const deleteItems = async (fromYear: number) => {
    try {
      setIsLoading(false);
      setIsDeleting(true);
      const isDeleteSuccessful = await deleteYear(budgetGuid, fromYear);
      if (isDeleteSuccessful) {
        setIsDeleting(false);
        Toaster.show({
          message: `Successfully deleted all items from ${fromYear}`,
          intent: Intent.SUCCESS,
          icon: 'tick-circle',
        });
        setBudgetYear(fromYear - 1);
        history.push(`${APP.ROUTES.LEDGER}/${fromYear - 1}`);
      }
    } catch (err) {
      console.error(err);
      setIsDeleting(false);
      Toaster.show({
        message: `An error occurred while trying to delete items for ${fromYear}`,
        intent: Intent.DANGER,
        icon: 'error',
      });
    }
  };

  return (
    <div className={classes.ledger}>
      <div className={classes.ledgerLeft}>
        <LedgerNav scrollToMonth={scrollToMonth} percentScrolled={percentScrolled} />
      </div>
      <div className={classes.ledgerRight}>
        <Table
          categorize={categorize}
          confirmDeletion={confirmDeletion}
          addItem={addItem}
          editItem={editItem}
          editMultipleItems={editMultipleItems}
          copyItems={copyItems}
          deleteItems={deleteItems}
          scrollToMonth={scrollToMonth}
          isLoading={isLoading}
          isCopying={isCopying}
          isDeleting={isDeleting}
          isEmpty={isEmpty}
          nextYearItemCount={nextYearItemCount}
          setPercentScrolled={setPercentScrolled}
        />
      </div>
      <Dialog
        isOpen={isCategorizeDialogOpen && !!itemToCategorize}
        title="Assign Categories"
        onClose={closeCategorizeDialog}
        message={dialogMessage}
        cancelLabel="Cancel"
        cancelIntent={Intent.NONE}
        onCancel={closeCategorizeDialog}
        actionLabel="Save"
        actionIntent={Intent.PRIMARY}
        actionIcon="saved"
        onAction={async () => await categorizeItem()}
      >
        <Categories
          itemToCategorize={itemToCategorize}
          setCategoryToCreate={setCategoryToCreate}
          setSubcategoryToCreate={setSubcategoryToCreate}
        />
      </Dialog>
      <Dialog
        isOpen={isDeleteDialogOpen && !!itemToDelete}
        title="Confirm deletion"
        onClose={closeDeleteDialog}
        message={dialogMessage}
        cancelLabel="Cancel"
        cancelIntent={Intent.NONE}
        onCancel={closeDeleteDialog}
        actionLabel="Delete"
        actionIntent={Intent.DANGER}
        actionIcon="trash"
        onAction={async () => await deleteItem()}
      />
    </div>
  );
};
